import { RefObject } from 'react';
import { SceneHander } from '@component/thumbnail';
import { DesignSet, PartsProduct } from '@app/react/const/type';
import {
    fetchKintekiModel,
    fetchEntekiModel,
    fetchMakiwaraModel,
} from '@app/react/common/loadModel';
import {
    fetchAndApply,
    fetchAndApply2,
    fetchAndApplyFamilycrestColor,
    fetchAndApplyMultiple,
    fetchAndApplyWithMakiwara,
} from '@app/react/3d/utils/thumbnail';
import * as TexHandler from '@app/react/3d/texHandler';
import { CARVING_TEXT_COLOR_TEX_PATH } from '@app/react/const/variables';
import { toRad } from '@app/react/common/toRad';

// 羽根が中央に来るようにする
const featherCenterPos = { x: -11, y: -10.5, z: 0 };

export const loadKintekiModelAndTexture = async (
    item: DesignSet,
    modelHandleRef: RefObject<SceneHander>,
): Promise<void> => {
    const model = await fetchKintekiModel(false).catch((err) => {
        console.error('[3d thumbnail] kinteki model load error', err);
        return null;
    });
    if (model === null) {
        return;
    }

    model.model.position.set(
        featherCenterPos.x,
        featherCenterPos.y,
        featherCenterPos.z,
    );
    model.model.rotation.set(0, 0, toRad(43));
    model.model.updateMatrixWorld();

    const promises = [];
    try {
        promises.push(fetchAndApply(item.shaft, TexHandler.shaft(model)));
        promises.push(fetchAndApply2(item.feather, TexHandler.kintekiHane(model)));
        promises.push(fetchAndApply2(item.nock, TexHandler.nock(model)));
        promises.push(
            fetchAndApply(
                item.thread,
                TexHandler.thread(model, item.threadGloss),
            ),
        );
        promises.push(fetchAndApply(item.kebiki, TexHandler.kebiki(model)));
        promises.push(fetchAndApply2(item.featherinside, TexHandler.featherinside(model)));
        promises.push(
            fetchAndApplyMultiple(
                item.decoration,
                'decoration',
                TexHandler.decoration(model),
            ),
        );
        promises.push(fetchAndApply2(item.lame, TexHandler.lame(model)));
        promises.push(fetchAndApply2(item.twine, TexHandler.twine(model)));
        promises.push(fetchAndApply(item.familycrest, TexHandler.familyCrest(model)));
        promises.push(
            fetchAndApplyFamilycrestColor(
                item.familycrestColor,
                TexHandler.familyCrestColor(model),
            ),
        );
        promises.push(
            fetchAndApply(
                { textureImages: [CARVING_TEXT_COLOR_TEX_PATH] } as PartsProduct,
                TexHandler.carvingColor(model),
            ),
        );

        TexHandler.carving(model, item.carving, item.carvingString ?? '');

        await Promise.all(promises);
        modelHandleRef.current?.scene()?.add(model.model);
    } catch (err) {
        console.error('[3d thumbnail] kinteki texture load error', err);
    }
};

export const loadEntekiModelAndTexture = async (
    item: DesignSet,
    modelHandleRef: RefObject<SceneHander>,
): Promise<void> => {
    const model = await fetchEntekiModel(false).catch((err) => {
        console.error('[3d thumbnail] enteki model load error', err);
        return null;
    });
    if (model == null) {
        return;
    }

    model.model.position.set(
        featherCenterPos.x,
        featherCenterPos.y,
        featherCenterPos.z,
    );
    model.model.rotation.set(0, 0, toRad(43));
    model.model.updateMatrixWorld();

    const promises = [];
    try {
        promises.push(fetchAndApply(item.shaft, TexHandler.shaft(model)));
        promises.push(
            fetchAndApply2(item.feather, TexHandler.entekiHane(model)),
        );
        promises.push(fetchAndApply2(item.nock, TexHandler.nock(model)));
        promises.push(
            fetchAndApply(
                item.thread,
                TexHandler.thread(model, item.threadGloss),
            ),
        );
        promises.push(fetchAndApply(item.kebiki, TexHandler.kebiki(model)));
        promises.push(
            fetchAndApply2(item.featherinside, TexHandler.featherinside(model)),
        );
        promises.push(
            fetchAndApplyMultiple(
                item.decoration,
                'decoration',
                TexHandler.decoration(model),
            ),
        );
        promises.push(fetchAndApply2(item.lame, TexHandler.lame(model)));
        promises.push(fetchAndApply2(item.twine, TexHandler.twine(model)));
        promises.push(
            fetchAndApply(
                {
                    textureImages: [CARVING_TEXT_COLOR_TEX_PATH],
                } as PartsProduct,
                TexHandler.carvingColor(model),
            ),
        );

        TexHandler.carving(model, item.carving, item.carvingString ?? '');

        await Promise.all(promises);
        modelHandleRef.current?.scene()?.add(model.model);
    } catch (err) {
        console.error('[3d thumbnail] enteki texture load error', err);
    }
};

export const loadMakiwaraModelAndTexture = async (
    item: DesignSet,
    modelHandleRef: RefObject<SceneHander>,
): Promise<void> => {
    const model = await fetchMakiwaraModel(false).catch((err) => {
        console.error('[3d thumbnail] makiwara model load error', err);
        return null;
    });
    if (model == null) {
        return;
    }

    model.model.position.set(
        featherCenterPos.x,
        featherCenterPos.y,
        featherCenterPos.z,
    );
    model.model.rotation.set(0, 0, toRad(43));
    model.model.updateMatrixWorld();

    const promises = [];
    try {
        promises.push(fetchAndApply(item.shaft, TexHandler.shaft(model)));
        promises.push(
            fetchAndApplyWithMakiwara(
                item.feather,
                'feather',
                TexHandler.makiwaraHane(model),
                () => {
                    const h = TexHandler.nonFeather(model);
                    h(item.threadMode);
                },
            ),
        );
        promises.push(fetchAndApply2(item.nock, TexHandler.nock(model)));
        promises.push(
            fetchAndApply(
                item.thread,
                TexHandler.thread(model, item.threadGloss),
            ),
        );
        promises.push(fetchAndApply(item.kebiki, TexHandler.kebiki(model)));
        promises.push(
            fetchAndApply2(item.featherinside, TexHandler.featherinside(model)),
        );
        promises.push(
            fetchAndApplyMultiple(
                item.decoration,
                'decoration',
                TexHandler.decoration(model),
            ),
        );
        promises.push(fetchAndApply2(item.lame, TexHandler.lame(model)));
        promises.push(fetchAndApply2(item.twine, TexHandler.twine(model)));
        promises.push(
            fetchAndApply(
                {
                    textureImages: [CARVING_TEXT_COLOR_TEX_PATH],
                } as PartsProduct,
                TexHandler.carvingColor(model),
            ),
        );

        TexHandler.carving(model, item.carving, item.carvingString ?? '');

        await Promise.all(promises);
        modelHandleRef.current?.scene()?.add(model.model);
    } catch (err) {
        console.error('[3d thumbnail] makiwara texture load error', err);
    }
};
